import { React, useState } from "react";
import PropTypes from "prop-types";
import ReactPaginate from "react-paginate";
import "../../styles/Overflow.css";
import CompanyBillingEmail from "./CompanyBillingEmail";
import CompanyPaymentUpdate from "./CompanyPaymentUpdate";
import { localDate, localDatePlus30 } from "../Utils/DateHelpers";

function BillingTable({
  companies,
  updateTable,
  setUpdateTable,
  setOpenSnackbar,
  setSnackbarMessage,
}) {
  const toBeInvoiced = companies.filter(
    (company) =>
      company.status.status === "New" || company.status.status === "Renew"
  );
  const awaitingPayment = companies.filter(
    (company) =>
      company.status.status === "BilledNew" ||
      company.status.status === "BilledRenew"
  );
  const activeCompanies = companies.filter(
    (company) => company.status.status === "Active"
  );

  const itemsPerPage = 5;
  const [invoiceOffset, setInvoiceOffset] = useState(0);
  const [paymentOffset, setPaymentOffset] = useState(0);
  const [activeOffset, setActiveOffset] = useState(0);
  const [activeTab, setActiveTab] = useState("invoice");

  const endInvoiceOffset = invoiceOffset + itemsPerPage;
  const currentToBeInvoiced = toBeInvoiced.slice(
    invoiceOffset,
    endInvoiceOffset
  );
  const invoicePageCount = Math.ceil(toBeInvoiced.length / itemsPerPage);
  const handleInvoicePageClick = (event) => {
    setInvoiceOffset((event.selected * itemsPerPage) % toBeInvoiced.length);
  };

  const endPaymentOffset = paymentOffset + itemsPerPage;
  const currentAwaitingPayment = awaitingPayment.slice(
    paymentOffset,
    endPaymentOffset
  );
  const paymentPageCount = Math.ceil(awaitingPayment.length / itemsPerPage);
  const handlePaymentPageClick = (event) => {
    setPaymentOffset((event.selected * itemsPerPage) % awaitingPayment.length);
  };

  const endActiveOffset = activeOffset + itemsPerPage;
  const currentActiveCompanies = activeCompanies.slice(
    activeOffset,
    endActiveOffset
  );
  const activePageCount = Math.ceil(activeCompanies.length / itemsPerPage);
  const handleActivePageClick = (event) => {
    setActiveOffset((event.selected * itemsPerPage) % activeCompanies.length);
  };

  return (
    <div className="BillingTable bg-white p-6 rounded-xl w-full">
      {/* Tab Toggle */}
      <div className="flex mb-6 border-b border-gray-300 shadow-sm">
        <button
          onClick={() => setActiveTab("invoice")}
          className={`flex-1 py-2 text-center font-bold transition ${
            activeTab === "invoice"
              ? "border-b-4 border-brand-primary text-brand-primary"
              : "text-gray-500"
          }`}>
          To Be Invoiced
        </button>
        <button
          onClick={() => setActiveTab("payment")}
          className={`flex-1 py-2 text-center font-bold transition ${
            activeTab === "payment"
              ? "border-b-4 border-brand-primary text-brand-primary"
              : "text-gray-500"
          }`}>
          Awaiting Payment
        </button>
        <button
          onClick={() => setActiveTab("active")}
          className={`flex-1 py-2 text-center font-bold transition ${
            activeTab === "active"
              ? "border-b-4 border-brand-primary text-brand-primary"
              : "text-gray-500"
          }`}>
          Active
        </button>
      </div>

      {/* To Be Invoiced Table */}
      {activeTab === "invoice" && (
        <>
          {currentToBeInvoiced.length === 0 ? (
            <p className="text-gray-500">No companies found.</p>
          ) : (
            <div className="CompanyTable bg-white p-2 rounded-xl w-full">
              <div className="CompanyTable-container flex rounded-md border-2 border-black drop-shadow-2xl mb-1">
                <div className="text-xl flex justify-center font-brand-primary w-2/12 border-black border-r-2 py-2">
                  Name
                </div>
                <div className="text-xl flex justify-center font-brand-primary w-2/12 border-black border-r-2 py-2">
                  Partnership Level
                </div>
                <div className="text-xl flex justify-center font-brand-primary w-3/12 border-black border-r-2 py-2">
                  Start Date
                </div>
                <div className="text-xl flex justify-center font-brand-primary w-3/12 border-black border-r-2 py-2">
                  End Date
                </div>
                <div className="text-xl flex justify-center font-brand-primary w-2/12 border-black py-2">
                  Billing Action
                </div>
              </div>
              {currentToBeInvoiced.map((company, index) => (
                <div
                  key={index}
                  className="CompanyRow-container flex rounded-md border-2 border-brand-tertiary drop-shadow-2xl mb-2">
                  <div className="text-xl flex justify-center font-brand-primary w-2/12 border-r-2 py-2">
                    {company.name}
                  </div>
                  <div className="text-xl flex justify-center font-brand-primary w-2/12 border-r-2 py-2">
                    {company.membership.membershipType}
                  </div>
                  <div className="text-xl flex justify-center font-brand-primary w-3/12 border-r-2 py-2">
                    {new localDate(company.startDate).toLocaleDateString()}
                  </div>
                  <div className="text-xl flex justify-center font-brand-primary w-3/12 border-r-2 py-2">
                    {new localDate(company.endDate).toLocaleDateString()}
                  </div>
                  <div className="text-xl flex justify-center font-brand-primary w-2/12 py-2">
                    <CompanyBillingEmail
                      company={company}
                      updateTable={updateTable}
                      setUpdateTable={setUpdateTable}
                      setOpenSnackbar={setOpenSnackbar}
                      setSnackbarMessage={setSnackbarMessage}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          {invoicePageCount > 1 && (
            <ReactPaginate
              breakLabel="..."
              nextLabel=">"
              onPageChange={handleInvoicePageClick}
              pageRangeDisplayed={3}
              pageCount={invoicePageCount}
              previousLabel="<"
              renderOnZeroPageCount={null}
              className="flex w-full justify-between mb-6 mt-4 [&>li>a]:py-4 [&>li>a]:px-6 [&>li>a]:border [&>li>a]:rounded-lg [&>li.selected>a]:border-brand-primary"
            />
          )}
        </>
      )}

      {/* Awaiting Payment Table */}
      {activeTab === "payment" && (
        <>
          {currentAwaitingPayment.length === 0 ? (
            <p className="text-gray-500">No companies found.</p>
          ) : (
            <div className="CompanyTable bg-white p-2 rounded-xl w-full">
              <div className="CompanyTable-container flex rounded-md border-2 border-black drop-shadow-2xl mb-1">
                <div className="text-xl flex justify-center font-brand-primary w-2/12 border-black border-r-2 py-2">
                  Name
                </div>
                <div className="text-xl flex justify-center font-brand-primary w-2/12 border-black border-r-2 py-2">
                  Partnership Level
                </div>
                <div className="text-xl flex justify-center font-brand-primary w-3/12 border-black border-r-2 py-2">
                  Invoice Date
                </div>
                <div className="text-xl flex justify-center font-brand-primary w-3/12 border-black border-r-2 py-2">
                  Due Date
                </div>
                <div className="text-xl flex justify-center font-brand-primary w-2/12 py-2">
                  Billing Action
                </div>
              </div>
              {currentAwaitingPayment.map((company, index) => (
                <div
                  key={index}
                  className="CompanyRow-container flex rounded-md border-2 border-brand-tertiary drop-shadow-2xl mb-2">
                  <div className="text-xl flex justify-center font-brand-primary w-2/12 border-r-2 py-2">
                    {company.name}
                  </div>
                  <div className="text-xl flex justify-center font-brand-primary w-2/12 border-r-2 py-2">
                    {company.membership.membershipType}
                  </div>
                  <div className="text-xl flex justify-center font-brand-primary w-3/12 border-r-2 py-2">
                    {new localDate(company.billingDate).toLocaleDateString()}
                  </div>
                  <div className="text-xl flex justify-center font-brand-primary w-3/12 border-r-2 py-2">
                    {localDatePlus30(company.billingDate).toLocaleDateString()}
                  </div>
                  <div className="text-xl flex justify-center font-brand-primary w-2/12 py-2">
                    <CompanyPaymentUpdate
                      company={company}
                      updateTable={updateTable}
                      setUpdateTable={setUpdateTable}
                      setOpenSnackbar={setOpenSnackbar}
                      setSnackbarMessage={setSnackbarMessage}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          {paymentPageCount > 1 && (
            <ReactPaginate
              breakLabel="..."
              nextLabel=">"
              onPageChange={handlePaymentPageClick}
              pageRangeDisplayed={3}
              pageCount={paymentPageCount}
              previousLabel="<"
              renderOnZeroPageCount={null}
              className="flex w-full justify-between mb-6 mt-4 [&>li>a]:py-4 [&>li>a]:px-6 [&>li>a]:border [&>li>a]:rounded-lg [&>li.selected>a]:border-brand-primary"
            />
          )}
        </>
      )}

      {/* Active Companies Table */}
      {activeTab === "active" && (
        <>
          {currentActiveCompanies.length === 0 ? (
            <p className="text-gray-500">No companies found.</p>
          ) : (
            <div className="CompanyTable bg-white p-2 rounded-xl w-full">
              <div className="CompanyTable-container flex rounded-md border-2 border-black drop-shadow-2xl mb-1">
                <div className="text-xl flex justify-center font-brand-primary w-2/12 border-black border-r-2 py-2">
                  Name
                </div>
                <div className="text-xl flex justify-center font-brand-primary w-2/12 border-black border-r-2 py-2">
                  Partnership Level
                </div>
                <div className="text-xl flex justify-center font-brand-primary w-2/12 border-black border-r-2 py-2">
                  Start Date
                </div>
                <div className="text-xl flex justify-center font-brand-primary w-2/12 border-black border-r-2 py-2">
                  End Date
                </div>
                <div className="text-xl flex justify-center font-brand-primary w-2/12 border-black border-r-2 py-2">
                  Billing Date
                </div>
                <div className="text-xl flex justify-center font-brand-primary w-2/12 py-2">
                  Payment Date
                </div>
              </div>
              {currentActiveCompanies.map((company, index) => (
                <div
                  key={index}
                  className="CompanyRow-container flex rounded-md border-2 border-brand-tertiary drop-shadow-2xl mb-2">
                  <div className="text-xl flex justify-center font-brand-primary w-2/12 border-r-2 py-2">
                    {company.name}
                  </div>
                  <div className="text-xl flex justify-center font-brand-primary w-2/12 border-r-2 py-2">
                    {company.membership.membershipType}
                  </div>
                  <div className="text-xl flex justify-center font-brand-primary w-2/12 border-r-2 py-2">
                    {new localDate(company.startDate).toLocaleDateString()}
                  </div>
                  <div className="text-xl flex justify-center font-brand-primary w-2/12 border-r-2 py-2">
                    {new localDate(company.endDate).toLocaleDateString()}
                  </div>
                  <div className="text-xl flex justify-center font-brand-primary w-2/12 border-r-2 py-2">
                    {new localDate(company.billingDate).toLocaleDateString()}
                  </div>
                  <div className="text-xl flex justify-center font-brand-primary w-2/12 py-2">
                    {new localDate(company.paymentDate).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
          {activePageCount > 1 && (
            <ReactPaginate
              breakLabel="..."
              nextLabel=">"
              onPageChange={handleActivePageClick}
              pageRangeDisplayed={3}
              pageCount={activePageCount}
              previousLabel="<"
              renderOnZeroPageCount={null}
              className="flex w-full justify-between mb-6 mt-4 [&>li>a]:py-4 [&>li>a]:px-6 [&>li>a]:border [&>li>a]:rounded-lg [&>li.selected>a]:border-brand-primary"
            />
          )}
        </>
      )}
    </div>
  );
}

BillingTable.propTypes = {
  companies: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      membership: PropTypes.shape({
        membershipType: PropTypes.string.isRequired,
      }).isRequired,
      billingDate: PropTypes.string,
      paymentDate: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
    })
  ),
  updateTable: PropTypes.func.isRequired,
  setUpdateTable: PropTypes.func.isRequired,
  setOpenSnackbar: PropTypes.func.isRequired,
  setSnackbarMessage: PropTypes.func.isRequired,
};

BillingTable.defaultProps = {
  companies: [],
};

export default BillingTable;
